import { Injectable } from '@angular/core';
import { ConfigurationService, LoadScriptService, SubscriptionCollector, TrackingV2Service } from '@draeger-academy/common';

import { BehaviorSubject } from 'rxjs';
import { CookieConsentOverlayService } from '../../../cookie-consent-overlay/cookie-consent-overlay.service';

@Injectable()
export class HubspotTrackingService {
    @SubscriptionCollector() private _subscriptions;
    private readonly _trackingScriptUrl = 'https://js.hs-scripts.com/3926732.js';
    private _trackingScriptState: undefined | 'loading' | 'loaded' | 'error';
    private _queuedTracking = [];
    private _$interactionEventSent: { [key: string]: BehaviorSubject<boolean> } = {};
    private _currentFormId: string;

    private _formType?: string;
    private _formName?: string;
    private uniqueForm = {
        status: false,
        form: {
            id: null,
            type: null,
            name: null,
            status: ''
        }
    };

    constructor(
        private _loadScriptService: LoadScriptService,
        private _trackingService: TrackingV2Service,
        private _cookieConsentOverlayService: CookieConsentOverlayService,
        private _configurationService: ConfigurationService
    ) {
        window['_hsq'] = window['_hsq'] || [];
    }

    public loadTrackingScript(formId: string): void {
        this._currentFormId = formId;
        this.uniqueForm = {
            status: false,
            form: {
                id: this._currentFormId,
                type: this._formType,
                name: this._formName,
                status: ''
            }
        };
        this.loadTrackingScriptCookies();
    }

    public loadTrackingScriptCookies(): void {
        if (!this._trackingScriptState) {
            this._trackingScriptState = 'loading';
            const _alreadyLoaded = document.querySelector('script[src="//js.hs-scripts.com/3926732.js"]');
            if (_alreadyLoaded) {
                this._trackingScriptState = 'loaded';
                this._catchUpTracking();
            } else {
                this._loadScriptService
                    .load('hs-script-loader', this._trackingScriptUrl)
                    .then((message) => {
                        this._trackingScriptState = 'loaded';
                        this._catchUpTracking();
                        if (this._cookieConsentOverlayService.cookieConsentFeatureActiveCheck()) {
                            this._checkCookieConsent();
                        } else if (this._configurationService.isPartnerPage()) {
                            this._cookieConsentOverlayService.cookieConsentFeatureActiveCheckInPortal();

                            setTimeout(() => {
                                if (this._cookieConsentOverlayService.cookieConsentActive) {
                                    this._checkCookieConsent();
                                }
                            }, 500);
                        } else {
                            this._allowTrack();
                        }
                    })
                    .catch((error) => {
                        this._trackingScriptState = 'error';
                    });
            }
        }
    }

    private _checkCookieConsent() {
        // this._cookieConsentOverlayService.checkSiteCookieConsent();
        const _cookieConsent = this._cookieConsentOverlayService.cookieConsentData$.getValue();
        this._updateTrack(_cookieConsent.Statistics);
        this._subscriptions.push(
            this._cookieConsentOverlayService.cookieConsentData$.subscribe((value) => {
                this._updateTrack(value.Statistics);
            })
        );
    }

    private _updateTrack(value: number) {
        if (value === 0) {
            window['_hsq'].push(['revokeCookieConsent']);
            window['_hsq'].push(['doNotTrack']);

        } else {
            this._allowTrack();
        }
    }

    private _allowTrack() {
        window['_hsq'].push(['doNotTrack', { track: true }]);
    }

    public trackSubmit(guid: string) {
        this._trackingService.trackEvent({
            action: 'form.sent',
            data: {
                form: {
                    type: 'general contact',
                    name: guid
                }
            }
        });
        this.uniqueForm.status = false;
    }

    public trackFormReady(guid: string) {
        this._trackEvent('trackFormView', guid);
    }

    public trackInView(guid: string) {
        this._trackEvent('trackFormVisible', guid);
    }

    public trackFirstFormInteraction(guid: string) {
        if (!this._$interactionEventSent[guid]) {
            this._trackingService.trackEvent({
                action: 'form.open',
                data: {
                    form: {
                        type: 'general contact',
                        name: guid
                    }
                }
            });
            this._trackEvent('trackFormInteraction', guid);
            if (!this._$interactionEventSent[guid]) {
                this._$interactionEventSent[guid] = new BehaviorSubject(null);
            }
            this._$interactionEventSent[guid].next(true);
        }
    }

    private _trackEvent(trackingAction: string, guid: string): void {
        this._track([
            trackingAction,
            guid,
            null,
            {
                formType: 0,
                formVariantId: null
            }
        ]);
    }

    private _track(trackingObject: any): void {
        if (this._trackingScriptState === 'loaded') {
            if (window) {
                window['_hsq'].push(trackingObject);
            }
        } else {
            this._queuedTracking.push(trackingObject);
        }
    }

    private _catchUpTracking(): void {
        for (const _queuedTrackingEntry of this._queuedTracking) {
            window['_hsq'].push(_queuedTrackingEntry);
        }
        this._queuedTracking = undefined;
    }
}
